import { InfoEmployeds } from 'components/InfoEmployeds';
import { FC, useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useGetOrderQuery, useGetWorkersQuery } from 'redux/Api/ordersApi/ordersApi';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { RootState } from 'redux/store';
import { Button } from 'ui/Button';
import { InputSearch } from 'ui/InputSearch';
import { getFormatPhone } from 'utils/getFotmatPhone';
import close from './img/iconClose.svg';
import styles from './OrderExecutor.module.scss';
import { ReactComponent as CheckIcon } from './img/check.svg';
import { ReactComponent as AlertIcon } from './img/alert.svg';
import { ReactComponent as WarningIcon } from './img/iconWarning.svg';
import { ReactComponent as IconError } from './img/iconError.svg';
import { Avatar } from 'components/Avatar';
import moment from 'moment';
import { useCanManage } from 'hooks/useCanManage';
import MinorAgeChecker from 'components/MinorAgeChecker';
import { GetWorkersArgs, ResponseGetWorkerType } from 'redux/Api/ordersApi/type';
import { EmployedsFilterParams, Filters } from 'pages/EmployedsContent/Filters/Filters';
import { useGetFiltersEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { useMediaQuery } from '@mantine/hooks';

interface OrderExecutorTypes {
  errors?: {
    employee?: string;
  };
  setContractLimit?: (contractLimit: number | null) => void;
}

type SearchParamsState = GetWorkersArgs | undefined;

export const OrderExecutor: FC<OrderExecutorTypes> = ({ errors, setContractLimit }) => {
  const canManage = useCanManage();
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const { data: dataFilters } = useGetFiltersEmployedsQuery();

  const isMediumWidth =
    useMediaQuery('(min-width: 1200px) and (max-width: 1406px)') ?? false;
  const isSmallWidth = useMediaQuery('(max-width: 767px)') ?? false;

  const dispatch = useDispatch();
  const { id } = useParams();
  const { data, isLoading } = useGetOrderQuery({ id });
  const [searchParams, setSearachParams] = useState<SearchParamsState>(undefined);
  const {
    data: workersData,
    isLoading: workersDataIsLoading,
    isFetching: workersFetching,
  } = useGetWorkersQuery(searchParams);

  const [filtersParams, setFiltersParams] = useState<EmployedsFilterParams>({
    genders: [],
    issues: [],
    nationalities: [],
    categories: [],
    contracts: [],
    limit_from: undefined,
    limit_to: undefined,
    date_add: undefined,
  });

  const applySearchParams = (next: SearchParamsState) => {
    if (!setSearachParams) return;
    setSearachParams(next);
  };

  const convertFiltersToApiParams = (filters: EmployedsFilterParams) => {
    return {
      genders: filters.genders,
      issues: filters.issues,
      categories: filters.categories?.map((c) => c.id),
      nationalities: filters.nationalities,
      contracts: filters.contracts,
      limit_from: filters.limit_from,
      limit_to: filters.limit_to,
      date_add: filters.date_add,
    };
  };

  const buildMergedParams = (nextSearch: string, nextFilters: EmployedsFilterParams) => {
    const hasSearch = Boolean(nextSearch.trim());
    const hasAnyFilters =
      (nextFilters.genders?.length ?? 0) > 0 ||
      (nextFilters.issues?.length ?? 0) > 0 ||
      (nextFilters.nationalities?.length ?? 0) > 0 ||
      (nextFilters.categories?.length ?? 0) > 0 ||
      (nextFilters.contracts?.length ?? 0) > 0 ||
      nextFilters.limit_from != null ||
      nextFilters.limit_to != null ||
      nextFilters.date_add?.start != null ||
      nextFilters.date_add?.end != null;

    if (!hasSearch && !hasAnyFilters) return undefined;

    const apiFilters = convertFiltersToApiParams(nextFilters);

    return {
      ...apiFilters,
      ...(hasSearch ? { search: nextSearch.trim() } : {}),
    };
  };

  const workersList = workersData?.data ?? [];

  const orderWorker = data?.worker?.id
    ? workersList.find((el) => el.id === data.worker.id)
    : undefined;

  useEffect(() => {
    setContractLimit?.(orderWorker?.unified_contract?.limit ?? null);
  }, [orderWorker]);

  const isCanceled = data?.status?.value === 'order_canceled';

  const selectedWorker = data?.is_draft
    ? workersList.find((worker) => worker.id === infoOrderDetail.worker_id)
    : (data?.worker as ResponseGetWorkerType | undefined);

  const [searchValue, setSearchValue] = useState('');

  if (data?.status?.value === 'moderation_in_progress') return null;
  if (isLoading) return null;

  const selectWorker = (id: number | null) => {
    dispatch(setInfoOrderDetail({ key: 'worker_id', value: id }));
  };

  const worker = selectedWorker as ResponseGetWorkerType | undefined;

  const isHasSignedContract = worker?.unified_contract?.status === 'signed_by_worker';

  return (
    <div className={styles.employees}>
      <div className={styles.employees_inner__top}>
        <p className={styles.employees_inner__title}>Исполнители</p>
        {canManage && data?.is_draft && selectedWorker && (
          <Button
            theme='link'
            icon
            src={close}
            onClick={() => {
              selectWorker(null);
            }}>
            Сбросить
          </Button>
        )}
      </div>
      {workersDataIsLoading ? (
        <ContentLoader
          speed={2}
          height={56}
          backgroundColor={'#FFFFFF'}
          foregroundColor={'#D8E4FB8C'}>
          <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
        </ContentLoader>
      ) : worker ? (
        <div className={styles.selected}>
          <div className={styles.employee_details}>
            <div className={styles.employee_header}>
              <Avatar
                name={worker?.name}
                surname={worker?.surname}
                avatar={worker?.worker_app?.avatar ?? worker?.avatar ?? null}
                id={worker?.id}
                statusActive={''}
                height='82px'
                width='82px'
                isDeleted={worker?.is_deleted}
              />
              <div className={styles.employee_info}>
                <div className={styles.employee_name_wrapper}>
                  <h3 className={styles.employee_name}>
                    {worker?.surname} {worker?.name} {worker?.patronymic}
                  </h3>
                  <p className={styles.employee_phone}>{getFormatPhone(worker?.phone)}</p>
                </div>
                <p className={styles.employee_inn}>ИНН {worker?.inn}</p>
                <p className={styles.risk_level}>Риск минимальный</p>
              </div>
            </div>

            <div className={styles.status_indicators}>
              {isHasSignedContract && (
                <div className={styles.status_item}>
                  <div className={styles.status_icon}>
                    <CheckIcon />
                  </div>
                  <p>Договор № УД-{selectedWorker?.unified_contract?.number}</p>
                </div>
              )}
              {isHasSignedContract && (
                <div className={styles.status_item}>
                  <div className={styles.status_icon}>
                    <CheckIcon />
                  </div>
                  <p>
                    Лимит по договору:{' '}
                    {selectedWorker?.unified_contract?.limit
                      ? `${
                          selectedWorker?.unified_contract?.total_orders_amount?.toLocaleString(
                            'ru-RU',
                          ) || 0
                        } ₽ из ${selectedWorker?.unified_contract?.limit?.toLocaleString(
                          'ru-RU',
                        )} ₽`
                      : 'Не указан'}
                  </p>
                </div>
              )}
              {worker?.limit && !isCanceled && (
                <div className={styles.status_item}>
                  <div className={styles.status_icon}>
                    <CheckIcon />
                  </div>
                  <p>
                    Лимит на месяц:{' '}
                    {worker?.monthly_payment?.toLocaleString('ru-RU') || '0'} ₽ из{' '}
                    {worker?.limit?.toLocaleString('ru-RU')} ₽
                  </p>
                </div>
              )}
              {!worker?.worker_app?.nationality ? (
                <div className={styles.status_item}>
                  <div className={styles.status_icon}>
                    <AlertIcon />
                  </div>
                  <p>Гражданство неизвестно. Обратитесь в поддержку</p>
                </div>
              ) : worker?.worker_app?.nationality !== 'Россия' ? (
                <div className={styles.status_item}>
                  <div className={styles.status_icon}>
                    <AlertIcon />
                  </div>
                  <p>
                    Не является гражданином России. Не забудь отправить уведомление в МВД
                  </p>
                </div>
              ) : null}

              <div className={styles.status_item}>
                <div className={styles.status_icon}>
                  <CheckIcon />
                </div>
                <p>Является самозанятым</p>
              </div>
              {Boolean(worker?.has_ip) && (
                <div className={styles.status_item}>
                  <div className={styles.status_icon}>
                    <WarningIcon />
                  </div>
                  <p>Есть открытое ИП</p>
                </div>
              )}
              <div className={styles.status_item}>
                <div className={styles.status_icon}>
                  <CheckIcon />
                </div>
                <p>Не состоит в списке экстремистов и террористов</p>
              </div>

              <div className={styles.status_item}>
                <div className={styles.status_icon}>
                  <CheckIcon />
                </div>
                <p>Лимит дохода НПД 2,4 млн. не израсходован</p>
              </div>
              {data?.worker?.is_deleted && (
                <div className={styles.status_item}>
                  <div className={styles.status_icon}>
                    <IconError style={{ width: '24px', height: '24px' }} />
                  </div>
                  <p style={{ color: '#e10d0d' }}>Профиль удален</p>
                </div>
              )}
              <div>
                <MinorAgeChecker data={orderWorker} showLabel={false} />
              </div>
            </div>

            <p className={styles.report_date}>
              Отчет на {moment(data?.status?.created_at).format('D MMMM YYYY')}
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div className={errors?.employee ? styles.border_error : undefined}>
            <div className={styles.searchLine}>
              <div style={{ width: '100%', minWidth: 0 }}>
                <InputSearch
                  notFocus
                  value={searchValue}
                  placeholder='Искать по ИНН, ФИО или номеру телефона'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchValue(e.target.value);
                    if (!e.target.value.trim()) {
                      applySearchParams(buildMergedParams('', filtersParams));
                    }
                  }}
                  onSearch={() => {
                    applySearchParams(buildMergedParams(searchValue, filtersParams));
                  }}
                />
              </div>
              <Filters
                dataFilters={
                  dataFilters?.filters ?? {
                    genders: [],
                    issues: [],
                    categories: [],
                    contracts: [],
                    nationalities: [],
                  }
                }
                notShowText={isMediumWidth || isSmallWidth}
                showFilters={true}
                initialFilters={{
                  genders: filtersParams.genders ?? [],
                  issues: filtersParams.issues ?? [],
                  nationalities: filtersParams.nationalities ?? [],
                  categories: filtersParams.categories ?? [],
                  contracts: filtersParams.contracts ?? [],
                  limit_from: filtersParams.limit_from,
                  limit_to: filtersParams.limit_to,
                  date_add: filtersParams.date_add,
                }}
                onApplyFilters={(nextFilters) => {
                  setFiltersParams({
                    genders: nextFilters.genders ?? [],
                    issues: nextFilters.issues ?? [],
                    nationalities: nextFilters.nationalities ?? [],
                    categories: nextFilters.categories ?? [],
                    contracts: nextFilters.contracts ?? [],
                    limit_from: nextFilters.limit_from,
                    limit_to: nextFilters.limit_to,
                    date_add: nextFilters.date_add,
                  });
                  applySearchParams(buildMergedParams(searchValue, nextFilters));
                }}
                onResetFilters={() => {
                  const empty: EmployedsFilterParams = {
                    genders: [],
                    issues: [],
                    nationalities: [],
                    categories: [],
                    contracts: [],
                    limit_from: undefined,
                    limit_to: undefined,
                    date_add: undefined,
                  };
                  setFiltersParams(empty);
                  applySearchParams(buildMergedParams(searchValue, empty));
                }}
              />
            </div>
            <div className={styles.employees_inner__table}>
              {workersFetching ? (
                <ContentLoader
                  speed={2}
                  height={'270px'}
                  width='100%'
                  className={styles.tab_loader}
                  backgroundColor={'#FFFFFF'}
                  foregroundColor={'#D8E4FB8C'}>
                  <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                </ContentLoader>
              ) : (
                <>
                  {workersList?.length ? (
                    workersList.map((worker) => (
                      <div
                        key={worker.id}
                        className={styles.employees_inner__table__item}
                        onClick={() => selectWorker(worker.id)}>
                        <div className={styles.employees_inner__table__item_user}>
                          <InfoEmployeds
                            status={worker.worker_app?.app_status}
                            avatar={worker.worker_app?.avatar ?? null}
                            id={worker.id}
                            name={worker.name}
                            surname={worker.surname}
                            patronymic={worker.patronymic}
                          />
                        </div>
                        <p className={styles.employees_inner__table__item__phone}>
                          {getFormatPhone(worker.phone) || ''}
                        </p>

                        <div className={styles.employees_inner__table__item_user_mobile}>
                          <InfoEmployeds
                            status={worker.worker_app?.app_status}
                            avatar={worker.worker_app?.avatar ?? null}
                            id={worker.id}
                            name={worker.name}
                            surname={worker.surname}
                            patronymic={worker.patronymic}
                            phone={worker.phone ?? undefined}
                            withPhoneIcon={false}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.employees_inner__table__empty}>
                      Нет исполнителей
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <p className={styles.employees_inner__info}>
            Предложить заказ можно только готовым к сотрудничеству исполнителям
          </p>
          <p className={styles.employees_inner__infoError}>{errors?.employee}</p>
        </div>
      )}
    </div>
  );
};
